import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import AdminAnalyticsTab from "../components/admin/AdminAnalyticsTab";
import AdminEmailTab from "../components/admin/AdminEmailTab";
import AdminTripEditor from "../components/admin/AdminTripEditor";
import { CheckCircle, XCircle, Clock, MapPin, Calendar, Users, User, RefreshCw, Shield, BarChart2, Mail, X, MessageSquare, Send, Feather, Pencil, ClipboardList, AlertTriangle, RotateCcw, ChevronDown, ShieldAlert, Gift } from "lucide-react";
import { toast } from "sonner";

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LORA = { fontFamily: "'Lora', Georgia, serif" };

function StatusBadge({ trip }) {
  if (trip.payment_status === "paid")
    return <span className="text-xs px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm" style={LORA}>Payé</span>;
  if (trip.admin_validated)
    return <span className="text-xs px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-sm" style={LORA}>Validé — en attente paiement</span>;
  if (trip.status === "rejeté")
    return <span className="text-xs px-2.5 py-1 bg-red-50 text-red-700 border border-red-200 rounded-sm" style={LORA}>Rejeté</span>;
  if (trip.status === "expiré")
    return <span className="text-xs px-2.5 py-1 bg-gray-100 text-gray-500 border border-gray-200 rounded-sm" style={LORA}>Expiré</span>;
  return <span className="text-xs px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm" style={LORA}>En attente de validation</span>;
}

function AdminChatPanel({ trip, onClose }) {
  const [messages, setMessages] = useState([
    { role: "bot", text: `Bonjour Diego ! Je suis le conseiller dédié à ce voyage vers ${trip.destination}. Testez-moi — posez n'importe quelle question sur cet itinéraire.` }
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/trips/${trip.id}/chat`, { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: "bot", text: data.response }]);
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur lors de la communication avec le conseiller.";
      setMessages((prev) => [...prev, { role: "bot", text: `⚠️ ${detail}` }]);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="mt-3 border border-[#C17F59]/30 bg-[#FDF8F3] animate-fade-in" style={{ borderRadius: "2px" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#DCD4C6]">
        <div className="flex items-center gap-2">
          <Feather size={13} strokeWidth={1.5} className="text-[#C17F59]" />
          <span className="text-sm" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>
            Conseiller — {trip.destination}
          </span>
          <span className="text-xs px-2 py-0.5 bg-[#C17F59]/10 text-[#8B5E34] border border-[#C17F59]/20" style={{ ...LORA, borderRadius: "2px" }}>
            Aperçu admin
          </span>
        </div>
        <button onClick={onClose} className="p-1 text-[#9A8F86] hover:text-[#2B2623] transition-colors" data-testid="admin-chat-close">
          <X size={14} strokeWidth={1.5} />
        </button>
      </div>

      {/* Messages */}
      <div className="overflow-y-auto p-3 space-y-2" style={{ maxHeight: "220px" }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`text-xs px-3 py-2 max-w-[85%] leading-relaxed ${
              msg.role === "user"
                ? "bg-[#C17F59] text-[#F9F6F0]"
                : "bg-white border border-[#DCD4C6] text-[#2B2623]"
            }`} style={{ ...LORA, borderRadius: "2px", whiteSpace: "pre-wrap" }}>
              {msg.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-[#DCD4C6] text-[#9A8F86] text-xs px-3 py-2 italic" style={{ ...LORA, borderRadius: "2px" }}>
              Le conseiller rédige...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex items-end gap-2 p-3 border-t border-[#DCD4C6]">
        <textarea
          className="flex-1 bg-white border border-[#DCD4C6] text-xs p-2 resize-none focus:outline-none focus:border-[#C17F59] transition-colors"
          rows={2}
          placeholder="Posez une question sur cet itinéraire..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          style={{ ...LORA, borderRadius: "1px" }}
          data-testid="admin-chat-input"
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim() || sending}
          className="w-8 h-8 bg-[#C17F59] text-[#F9F6F0] flex items-center justify-center hover:bg-[#A86A46] disabled:opacity-40 transition-colors flex-shrink-0"
          style={{ borderRadius: "1px" }}
          data-testid="admin-chat-send"
        >
          <Send size={12} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

// ── Admin Checklist Section ───────────────────────────────────────────────────
function AdminChecklistSection({ tripId, destination }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const loadChecklist = async () => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API}/trips/${tripId}/checklist`);
      setData(resp);
    } catch {
      toast.error("Impossible de charger la checklist");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!data) loadChecklist();
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await axios.post(`${API}/trips/${tripId}/checklist/regenerate`);
      toast.success("Régénération en cours...");
      setTimeout(loadChecklist, 3500);
    } catch {
      toast.error("Échec de la régénération");
    } finally {
      setRegenerating(false);
    }
  };

  const tasks = data?.tasks ?? [];
  const total = tasks.length;
  const completed = tasks.filter((t) => t.completed).length;
  const blockingUncompleted = tasks.filter((t) => t.bloquante && !t.completed);

  return (
    <div className="mt-3 border-t border-[#DCD4C6] pt-3">
      <button
        onClick={open ? () => setOpen(false) : handleOpen}
        className="flex items-center gap-2 text-sm text-[#5C554E] hover:text-[#C17F59] transition-colors"
        style={LORA}
        data-testid={`admin-checklist-toggle-${tripId}`}
      >
        <ClipboardList size={14} strokeWidth={1.5} />
        Checklist pré-départ
        <ChevronDown size={13} strokeWidth={1.5} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 p-4 bg-[#F9F6F0] border border-[#DCD4C6] rounded-sm">
          {loading ? (
            <p className="text-xs text-[#9A8F86] italic" style={LORA}>Chargement...</p>
          ) : !data || data.status === "not_available" ? (
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#9A8F86] italic" style={LORA}>Aucune checklist générée.</p>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#C17F59] text-white hover:bg-[#A86A46] disabled:opacity-50 transition-colors rounded-sm"
                style={LORA}
                data-testid={`admin-checklist-generate-${tripId}`}
              >
                <RotateCcw size={11} strokeWidth={2} className={regenerating ? "animate-spin" : ""} />
                Générer
              </button>
            </div>
          ) : data.status === "generating" ? (
            <p className="text-xs text-amber-600 italic" style={LORA}>Génération en cours...</p>
          ) : data.status === "generation_failed" ? (
            <div className="flex items-center justify-between">
              <p className="text-xs text-red-500 italic" style={LORA}>Génération échouée.</p>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#C17F59] text-white hover:bg-[#A86A46] disabled:opacity-50 transition-colors rounded-sm"
                style={LORA}
                data-testid={`admin-checklist-retry-${tripId}`}
              >
                <RotateCcw size={11} strokeWidth={2} className={regenerating ? "animate-spin" : ""} />
                Régénérer
              </button>
            </div>
          ) : (
            <div>
              {/* Summary row */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm text-[#2B2623] font-medium" style={LORA}>
                    {completed} / {total} tâches complétées
                  </div>
                  <div className="w-32 bg-[#DCD4C6] rounded-full h-1.5 mt-1 overflow-hidden">
                    <div
                      className="h-1.5 rounded-full transition-all"
                      style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%`, background: "#C17F59" }}
                    />
                  </div>
                </div>
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#DCD4C6] text-[#5C554E] hover:border-[#C17F59] hover:text-[#C17F59] disabled:opacity-50 transition-colors rounded-sm"
                  style={LORA}
                  data-testid={`admin-checklist-regen-${tripId}`}
                >
                  <RotateCcw size={11} strokeWidth={2} className={regenerating ? "animate-spin" : ""} />
                  {regenerating ? "En cours..." : "Régénérer"}
                </button>
              </div>

              {/* Blocking uncompleted tasks */}
              {blockingUncompleted.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-red-600 uppercase tracking-widest mb-2" style={LORA}>
                    Tâches bloquantes non complétées
                  </p>
                  {blockingUncompleted.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-1.5 rounded-sm">
                      <AlertTriangle size={11} strokeWidth={1.5} className="flex-shrink-0" />
                      <span style={LORA}>{t.titre}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Admin Scam Radar Section ──────────────────────────────────────────────────
function AdminScamRadarSection({ tripId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const loadRadar = async () => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API}/trips/${tripId}/scam-radar`);
      setData(resp);
    } catch {
      toast.error("Impossible de charger le radar");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!data) loadRadar();
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await axios.post(`${API}/trips/${tripId}/scam-radar/regenerate`);
      toast.success("Régénération du Voyager Malin en cours...");
      setData((prev) => prev ? { ...prev, status: "pending" } : null);
      setTimeout(loadRadar, 4000);
    } catch {
      toast.error("Échec de la régénération");
    } finally {
      setRegenerating(false);
    }
  };

  const arnaques = data?.arnaques ?? [];
  const categories = [...new Set(arnaques.map((a) => a.categorie))].filter(Boolean);

  const CAT_LABELS = {
    transport: "Transport", marche: "Marché", hebergement: "Hébergement",
    excursion: "Excursion", restauration: "Restauration",
    change: "Change", rue: "Rue", numerique: "Numérique", plage: "Plage",
  };

  const FREQ_DOT = { tres_frequent: "bg-red-500", frequent: "bg-amber-500", rare: "bg-slate-400" };

  return (
    <div className="mt-3 border-t border-[#DCD4C6] pt-3">
      <button
        onClick={open ? () => setOpen(false) : handleOpen}
        className="flex items-center gap-2 text-sm text-[#5C554E] hover:text-[#C17F59] transition-colors"
        style={LORA}
        data-testid={`admin-scam-toggle-${tripId}`}
      >
        <ShieldAlert size={14} strokeWidth={1.5} />
        Voyager Malin
        {data?.status === "completed" && arnaques.length > 0 && (
          <span className="text-xs px-1.5 py-0.5 bg-[#C17F59]/10 text-[#C17F59] border border-[#C17F59]/20 rounded-sm" style={LORA}>
            {arnaques.length} alerte{arnaques.length > 1 ? "s" : ""}
          </span>
        )}
        <ChevronDown size={13} strokeWidth={1.5} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 p-4 bg-[#F9F6F0] border border-[#DCD4C6] rounded-sm space-y-3">
          {loading ? (
            <p className="text-xs text-[#9A8F86] italic" style={LORA}>Chargement...</p>

          ) : !data || data.status === "not_available" ? (
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#9A8F86] italic" style={LORA}>Aucun radar généré.</p>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#C17F59] text-white hover:bg-[#A86A46] disabled:opacity-50 transition-colors rounded-sm"
                style={LORA}
                data-testid={`admin-scam-generate-${tripId}`}
              >
                <RotateCcw size={11} strokeWidth={2} className={regenerating ? "animate-spin" : ""} />
                Générer
              </button>
            </div>

          ) : data.status === "pending" ? (
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin flex-shrink-0" />
              <p className="text-xs text-amber-600 italic" style={LORA}>Génération en cours...</p>
            </div>

          ) : data.status === "failed" ? (
            <div className="flex items-center justify-between">
              <p className="text-xs text-red-500 italic" style={LORA}>Génération échouée.</p>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#C17F59] text-white hover:bg-[#A86A46] disabled:opacity-50 transition-colors rounded-sm"
                style={LORA}
                data-testid={`admin-scam-retry-${tripId}`}
              >
                <RotateCcw size={11} strokeWidth={2} className={regenerating ? "animate-spin" : ""} />
                Régénérer
              </button>
            </div>

          ) : (
            <>
              {/* Summary row */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#2B2623]" style={LORA}>
                    {arnaques.length} arnaque{arnaques.length > 1 ? "s" : ""} identifiée{arnaques.length > 1 ? "s" : ""}
                  </p>
                  {categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {categories.map((cat) => (
                        <span key={cat} className="text-xs px-2 py-0.5 bg-[#F1EBE1] border border-[#DCD4C6] text-[#5C554E] rounded-sm" style={LORA}>
                          {CAT_LABELS[cat] || cat}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#DCD4C6] text-[#5C554E] hover:border-[#C17F59] hover:text-[#C17F59] disabled:opacity-50 transition-colors rounded-sm flex-shrink-0"
                  style={LORA}
                  data-testid={`admin-scam-regen-${tripId}`}
                >
                  <RotateCcw size={11} strokeWidth={2} className={regenerating ? "animate-spin" : ""} />
                  {regenerating ? "En cours..." : "Régénérer"}
                </button>
              </div>

              {/* Conseil d'or preview */}
              {data.conseil_or && (
                <div className="flex items-start gap-2 bg-[#2B2623]/5 border-l-2 border-[#C17F59] pl-3 py-2">
                  <span className="text-[#C17F59] flex-shrink-0 mt-0.5">★</span>
                  <p className="text-xs text-[#5C554E] italic leading-relaxed" style={LORA}>{data.conseil_or}</p>
                </div>
              )}

              {/* Top 3 arnaques fréquentes */}
              {arnaques.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-widest text-[#8B5E34]" style={LORA}>Arnaques les plus fréquentes</p>
                  {arnaques
                    .filter((a) => a.frequence === "tres_frequent")
                    .slice(0, 3)
                    .map((a) => (
                      <div key={a.id} className="flex items-center gap-2 text-xs text-[#5C554E] bg-red-50 border border-red-100 px-3 py-1.5 rounded-sm">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${FREQ_DOT[a.frequence] || "bg-amber-500"}`} />
                        <span style={LORA}>{a.icone} {a.titre}</span>
                        {a.cout_moyen_si_piege > 0 && (
                          <span className="ml-auto text-[#9A8F86]">~{a.cout_moyen_si_piege} €</span>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function TripsTab({ trips, loading, fetchTrips, actionId, validate, reject, gift, pendingCount, validatedCount, paidCount, setEditTrip }) {
  const [openChatId, setOpenChatId] = useState(null);
  const [filter, setFilter] = useState("tous");
  const expiredCount = trips.filter((t) => t.status === "expiré").length;

  const FILTERS = [
    { id: "tous",      label: "Tous",        count: trips.length },
    { id: "attente",   label: "En attente",  count: pendingCount,    color: "amber" },
    { id: "valide",    label: "Validés",     count: validatedCount,  color: "blue" },
    { id: "rejete",    label: "Rejetés",     count: trips.filter((t) => t.status === "rejeté").length, color: "red" },
    { id: "expire",    label: "Expirés",     count: expiredCount,    color: "gray" },
    { id: "paye",      label: "Payés",       count: paidCount,       color: "emerald" },
  ];

  const filtered = trips.filter((t) => {
    if (filter === "tous")    return true;
    if (filter === "paye")    return t.payment_status === "paid";
    if (filter === "valide")  return t.admin_validated && t.payment_status !== "paid";
    if (filter === "rejete")  return t.status === "rejeté";
    if (filter === "expire")  return t.status === "expiré";
    if (filter === "attente") return !t.admin_validated && t.status !== "rejeté" && t.status !== "expiré";
    return true;
  });

  const colorMap = {
    amber:   { active: "bg-amber-500 text-white border-amber-500",     base: "border-amber-300 text-amber-700 hover:bg-amber-50" },
    blue:    { active: "bg-blue-500 text-white border-blue-500",       base: "border-blue-300 text-blue-700 hover:bg-blue-50" },
    red:     { active: "bg-red-500 text-white border-red-500",         base: "border-red-300 text-red-700 hover:bg-red-50" },
    gray:    { active: "bg-gray-500 text-white border-gray-500",       base: "border-gray-300 text-gray-600 hover:bg-gray-50" },
    emerald: { active: "bg-emerald-500 text-white border-emerald-500", base: "border-emerald-300 text-emerald-700 hover:bg-emerald-50" },
  };

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          { label: "En attente", value: pendingCount, color: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
          { label: "Validés", value: validatedCount, color: "text-blue-700", bg: "bg-blue-50 border-blue-200" },
          { label: "Payés", value: paidCount, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
        ].map((s, i) => (
          <div key={i} className={`card-surface border ${s.bg} text-center`} data-testid={`admin-stat-${i}`}>
            <p className={`text-3xl font-semibold ${s.color}`} style={SERIF}>{s.value}</p>
            <p className="text-xs uppercase tracking-widest text-[#5C554E] mt-1" style={LORA}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Refresh + Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        {/* Filter pills */}
        <div className="flex flex-wrap gap-1.5" data-testid="admin-filters">
          {FILTERS.map(({ id, label, count, color }) => {
            const isActive = filter === id;
            const cls = color ? colorMap[color] : null;
            const activeCls  = cls ? cls.active  : "bg-[#2B2623] text-[#F9F6F0] border-[#2B2623]";
            const baseCls    = cls ? cls.base    : "border-[#DCD4C6] text-[#5C554E] hover:bg-[#F1EBE1]";
            return (
              <button
                key={id}
                onClick={() => { setFilter(id); setOpenChatId(null); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-all ${isActive ? activeCls : baseCls}`}
                style={{ ...LORA, borderRadius: "2px" }}
                data-testid={`admin-filter-${id}`}
              >
                {label}
                <span className={`min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 ${
                  isActive ? "bg-white/25" : "bg-[#DCD4C6] text-[#5C554E]"
                }`}>{count}</span>
              </button>
            );
          })}
        </div>
        <button onClick={fetchTrips} className="btn-secondary flex items-center gap-2 text-sm flex-shrink-0" data-testid="admin-refresh-btn">
          <RefreshCw size={14} strokeWidth={1.5} /> Actualiser
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="w-8 h-8 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#9A8F86] italic" style={LORA}>Chargement des voyages...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 card-surface">
          <Shield size={32} strokeWidth={1.5} className="text-[#DCD4C6] mx-auto mb-4" />
          <p className="text-lg text-[#5C554E] italic" style={SERIF}>
            {filter === "tous" ? "Aucun voyage pour l'instant" : `Aucun voyage "${FILTERS.find(f => f.id === filter)?.label.toLowerCase()}"`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((trip) => (
            <div key={trip.id} className="card-surface animate-fade-in" data-testid={`admin-trip-${trip.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3 mb-2">
                    <h3 className="text-xl truncate" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>{trip.destination}</h3>
                    <StatusBadge trip={trip} />
                    {trip.is_gift && (
                      <span
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#C17F59]/10 text-[#C17F59] border border-[#C17F59]/30 flex-shrink-0"
                        style={{ ...LORA, borderRadius: "2px" }}
                        title="Accès offert par Diego"
                        data-testid={`gift-badge-${trip.id}`}
                      >
                        <Gift size={11} strokeWidth={1.5} />
                        Cadeau
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5C554E]" style={LORA}>
                    <span className="flex items-center gap-1"><User size={11} strokeWidth={1.5} className="text-[#C17F59]" /> {trip.user_name} — {trip.user_email}</span>
                    <span className="flex items-center gap-1"><Calendar size={11} strokeWidth={1.5} className="text-[#C17F59]" /> {trip.date_depart} → {trip.date_retour}</span>
                    <span className="flex items-center gap-1"><Users size={11} strokeWidth={1.5} className="text-[#C17F59]" /> {trip.nb_adultes} adulte{trip.nb_adultes > 1 ? "s" : ""}</span>
                    <span className="flex items-center gap-1"><MapPin size={11} strokeWidth={1.5} className="text-[#C17F59]" /> Budget : {trip.budget}</span>
                  </div>
                  {trip.created_at && (
                    <p className="text-xs text-[#9A8F86] mt-1 italic" style={LORA}>Généré le {new Date(trip.created_at).toLocaleDateString("fr-FR")}</p>
                  )}
                </div>

                <div className="flex gap-2 flex-shrink-0 flex-wrap">
                  {/* Edit button — available for all trips */}
                  <button
                    onClick={() => setEditTrip({ id: trip.id, destination: trip.destination })}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm border border-[#8B5E34]/30 text-[#8B5E34] hover:bg-[#8B5E34]/10 transition-colors"
                    style={{ ...LORA, borderRadius: "2px" }}
                    data-testid={`edit-trip-btn-${trip.id}`}
                    title="Modifier l'itinéraire"
                  >
                    <Pencil size={13} strokeWidth={1.5} />
                    Modifier
                  </button>

                  {/* Chatbot QA button */}
                  <button
                    onClick={() => setOpenChatId(openChatId === trip.id ? null : trip.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm border transition-colors ${
                      openChatId === trip.id
                        ? "bg-[#C17F59] text-[#F9F6F0] border-[#C17F59]"
                        : "border-[#C17F59]/40 text-[#C17F59] hover:bg-[#C17F59]/10"
                    }`}
                    style={{ ...LORA, borderRadius: "2px" }}
                    data-testid={`admin-chat-btn-${trip.id}`}
                    title="Tester le conseiller voyage sur cet itinéraire"
                  >
                    <MessageSquare size={13} strokeWidth={1.5} />
                    {openChatId === trip.id ? "Fermer" : "Tester le conseiller"}
                  </button>

                  {/* Gift VIP access — visible for all non-paid, non-expired trips */}
                  {trip.payment_status !== "paid" && trip.status !== "expiré" && (
                    <button
                      onClick={() => gift(trip.id)}
                      disabled={actionId === trip.id}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm border border-[#C17F59]/50 text-[#C17F59] hover:bg-[#C17F59] hover:text-[#F9F6F0] disabled:opacity-50 transition-colors"
                      style={{ ...LORA, borderRadius: "2px" }}
                      data-testid={`gift-btn-${trip.id}`}
                      title="Offrir un accès gratuit (VIP)"
                    >
                      <Gift size={13} strokeWidth={1.5} />
                      Offrir l'accès
                    </button>
                  )}

                  {!trip.admin_validated && trip.status !== "rejeté" && trip.status !== "expiré" && (
                    <button onClick={() => validate(trip.id)} disabled={actionId === trip.id}
                      className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50 transition-colors font-medium"
                      style={{ ...LORA, borderRadius: "2px" }} data-testid={`validate-btn-${trip.id}`}>
                      <CheckCircle size={14} strokeWidth={1.5} />
                      {actionId === trip.id ? "En cours..." : "Valider et envoyer"}
                    </button>
                  )}
                  {trip.admin_validated && trip.payment_status !== "paid" && (
                    <span className="flex items-center gap-1.5 px-4 py-2 text-sm text-emerald-700 border border-emerald-200" style={{ ...LORA, borderRadius: "2px" }}>
                      <CheckCircle size={14} strokeWidth={1.5} /> Validé
                    </span>
                  )}
                  {trip.payment_status === "paid" && (
                    <span className="flex items-center gap-1.5 px-4 py-2 text-sm text-[#C17F59] border border-[#DCD4C6]" style={{ ...LORA, borderRadius: "2px" }}>
                      Payé ✓
                    </span>
                  )}
                  {!trip.admin_validated && trip.status !== "rejeté" && trip.status !== "expiré" && (
                    <button onClick={() => reject(trip.id)} disabled={actionId === trip.id}
                      className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
                      style={{ ...LORA, borderRadius: "2px" }} data-testid={`reject-btn-${trip.id}`}>
                      <XCircle size={14} strokeWidth={1.5} />
                      Rejeter
                    </button>
                  )}
                  {trip.status === "rejeté" && (
                    <span className="flex items-center gap-1.5 px-4 py-2 text-sm text-red-600 border border-red-200" style={{ ...LORA, borderRadius: "2px" }}>
                      <XCircle size={14} strokeWidth={1.5} /> Rejeté
                    </span>
                  )}
                  {trip.status === "expiré" && (
                    <span className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-500 border border-gray-200" style={{ ...LORA, borderRadius: "2px" }}>
                      <Clock size={14} strokeWidth={1.5} /> Expiré
                    </span>
                  )}
                </div>
              </div>

              {/* Inline chatbot panel */}
              {openChatId === trip.id && (
                <AdminChatPanel trip={trip} onClose={() => setOpenChatId(null)} />
              )}

              {/* Checklist pré-départ section — visible for paid + validated trips */}
              {trip.payment_status === "paid" && trip.admin_validated && (
                <AdminChecklistSection tripId={trip.id} destination={trip.destination} />
              )}

              {/* Voyager Malin (scam radar) — visible for paid + validated trips */}
              {trip.payment_status === "paid" && trip.admin_validated && (
                <AdminScamRadarSection tripId={trip.id} />
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const TABS = [
  { id: "voyages", label: "Voyages", icon: Clock },
  { id: "analytiques", label: "Analytiques", icon: BarChart2 },
  { id: "email", label: "Email", icon: Mail },
];

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("voyages");
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/dashboard"); return; }
    fetchTrips();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/trips`);
      setTrips(data);
    } catch (err) {
      if (err.response?.status === 403) navigate("/dashboard");
      else toast.error("Impossible de charger les voyages");
    } finally {
      setLoading(false);
    }
  };

  const validate = async (tripId) => {
    setActionId(tripId);
    try {
      await axios.patch(`${API}/admin/trips/${tripId}/validate`, {});
      toast.success("Voyage validé avec succès !");
      setTrips((prev) => prev.map((t) => t.id === tripId ? { ...t, admin_validated: true } : t));
    } catch { toast.error("Erreur lors de la validation"); }
    finally { setActionId(null); }
  };

  const [rejectModal, setRejectModal] = useState(null); // { tripId, note }
  const [giftModal, setGiftModal] = useState(null);     // { tripId, planType }
  const [editTrip, setEditTrip] = useState(null); // { id, destination }

  const gift = (tripId) => setGiftModal({ tripId, planType: "serenite" });

  const confirmGift = async () => {
    if (!giftModal) return;
    const { tripId, planType } = giftModal;
    setGiftModal(null);
    setActionId(tripId);
    try {
      await axios.post(`${API}/admin/trips/${tripId}/gift-access`, { plan_type: planType });
      toast.success("Accès VIP offert — email envoyé au voyageur !");
      setTrips((prev) => prev.map((t) =>
        t.id === tripId
          ? { ...t, payment_status: "paid", status: "validé", admin_validated: true, is_gift: true, plan_type: planType }
          : t
      ));
    } catch { toast.error("Erreur lors de l'offre d'accès"); }
    finally { setActionId(null); }
  };

  const reject = (tripId) => {
    setRejectModal({ tripId, note: "" });
  };

  const confirmReject = async () => {
    if (!rejectModal) return;
    const { tripId, note } = rejectModal;
    setRejectModal(null);
    setActionId(tripId);
    try {
      await axios.patch(`${API}/admin/trips/${tripId}/reject`, { rejection_note: note });
      toast.success("Voyage rejeté" + (note.trim() ? " — note envoyée au voyageur" : ""));
      setTrips((prev) => prev.map((t) => t.id === tripId ? { ...t, status: "rejeté", admin_validated: false } : t));
    } catch { toast.error("Erreur lors du rejet"); }
    finally { setActionId(null); }
  };

  const pendingCount = trips.filter((t) => !t.admin_validated && t.status !== "rejeté" && t.status !== "expiré").length;
  const validatedCount = trips.filter((t) => t.admin_validated).length;
  const paidCount = trips.filter((t) => t.payment_status === "paid").length;
  const expiredCount = trips.filter((t) => t.status === "expiré").length;

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />

      {/* Gift Modal */}
      {giftModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: "rgba(43,38,35,0.55)" }} onClick={() => setGiftModal(null)}>
          <div className="w-full max-w-md mx-4 animate-fade-in-up" style={{ background: "#F1EBE1", border: "1px solid #DCD4C6", borderRadius: "3px" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 pb-3 border-b border-[#DCD4C6]">
              <div className="flex items-center gap-2">
                <Gift size={16} strokeWidth={1.5} className="text-[#C17F59]" />
                <h3 className="text-xl" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>Offrir l'accès (VIP)</h3>
              </div>
              <button onClick={() => setGiftModal(null)} className="p-1.5 text-[#9A8F86] hover:text-[#2B2623] transition-colors"><X size={16} strokeWidth={1.5} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-[#5C554E]" style={LORA}>
                Le voyageur recevra un email d'accès immédiat <strong>sans mention de paiement ni de facture</strong>. Un badge "Cadeau" apparaîtra dans la liste.
              </p>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#8B5E34] mb-2" style={LORA}>Offre à débloquer</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: "essentiel", label: "Essentiel", desc: "Itinéraire + infos" },
                    { key: "confort",   label: "Confort",   desc: "+ Budget + Culture" },
                    { key: "serenite", label: "Sérénité",  desc: "Accès complet" },
                  ].map(({ key, label, desc }) => (
                    <button
                      key={key}
                      onClick={() => setGiftModal((m) => ({ ...m, planType: key }))}
                      className={`p-3 text-left border transition-colors rounded-sm ${
                        giftModal.planType === key
                          ? "border-[#C17F59] bg-[#C17F59]/10 text-[#C17F59]"
                          : "border-[#DCD4C6] text-[#5C554E] hover:border-[#C17F59]/50"
                      }`}
                      style={LORA}
                      data-testid={`gift-plan-${key}`}
                    >
                      <p className="font-semibold text-sm">{label}</p>
                      <p className="text-xs opacity-70 mt-0.5">{desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setGiftModal(null)} className="flex-1 px-4 py-2.5 text-sm border border-[#DCD4C6] text-[#5C554E] hover:border-[#8B5E34] transition-colors" style={{ ...LORA, borderRadius: "2px" }} data-testid="gift-cancel-btn">Annuler</button>
                <button onClick={confirmGift} className="flex-1 px-4 py-2.5 text-sm bg-[#C17F59] text-white hover:bg-[#A86A46] transition-colors flex items-center justify-center gap-2" style={{ ...LORA, borderRadius: "2px" }} data-testid="gift-confirm-btn">
                  <Gift size={13} strokeWidth={1.5} /> Offrir l'accès
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: "rgba(43,38,35,0.55)" }} onClick={() => setRejectModal(null)}>
          <div className="w-full max-w-md mx-4 animate-fade-in-up" style={{ background: "#F1EBE1", border: "1px solid #DCD4C6", borderRadius: "3px" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 pb-3 border-b border-[#DCD4C6]">
              <div>
                <p className="text-xs text-red-600 uppercase tracking-widest mb-1" style={LORA}>Action irréversible</p>
                <h3 className="text-xl" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>Rejeter ce voyage ?</h3>
              </div>
              <button onClick={() => setRejectModal(null)} className="p-1.5 text-[#9A8F86] hover:text-[#2B2623] transition-colors"><X size={16} strokeWidth={1.5} /></button>
            </div>
            <div className="p-5">
              <p className="text-sm text-[#5C554E] mb-4" style={LORA}>
                Le voyageur recevra un email l'informant du rejet. Vous pouvez lui laisser une note explicative (optionnel).
              </p>
              <label className="block text-xs uppercase tracking-widest text-[#8B5E34] mb-2" style={LORA}>Note pour le voyageur (optionnel)</label>
              <textarea
                className="w-full border border-[#DCD4C6] bg-[#F9F6F0] p-3 text-sm text-[#2B2623] resize-none focus:outline-none focus:border-[#C17F59] transition-colors"
                style={{ ...LORA, borderRadius: "2px", minHeight: "80px" }}
                placeholder="Ex: La destination choisie nécessite des informations supplémentaires sur les dates..."
                value={rejectModal.note}
                onChange={(e) => setRejectModal((m) => ({ ...m, note: e.target.value }))}
                data-testid="rejection-note-input"
                maxLength={500}
              />
              <p className="text-xs text-[#9A8F86] text-right mt-1" style={LORA}>{rejectModal.note.length}/500</p>
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button onClick={() => setRejectModal(null)} className="flex-1 px-4 py-2.5 text-sm border border-[#DCD4C6] text-[#5C554E] hover:border-[#8B5E34] transition-colors" style={{ ...LORA, borderRadius: "2px" }} data-testid="reject-cancel-btn">Annuler</button>
              <button onClick={confirmReject} className="flex-1 px-4 py-2.5 text-sm bg-red-600 text-white hover:bg-red-700 transition-colors font-medium" style={{ ...LORA, borderRadius: "2px" }} data-testid="reject-confirm-btn">Confirmer le rejet</button>
            </div>
          </div>
        </div>
      )}
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <p className="section-label mb-2">Espace administrateur</p>
          <h1 className="text-4xl sm:text-5xl" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>
            Dashboard Diego
          </h1>
          <p className="text-sm text-[#5C554E] mt-1 italic" style={LORA}>
            Bonjour Diego — gérez vos voyages, vos analytics et votre configuration email
          </p>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-0 border-b border-[#DCD4C6] mb-8">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-all ${
                activeTab === id
                  ? "border-[#C17F59] text-[#C17F59]"
                  : "border-transparent text-[#9A8F86] hover:text-[#5C554E]"
              }`}
              style={LORA}
            >
              <Icon size={14} strokeWidth={1.5} />
              {label}
              {id === "voyages" && pendingCount > 0 && (
                <span className="ml-1 min-w-[18px] h-[18px] bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "voyages" && (
          <TripsTab
            trips={trips} loading={loading} fetchTrips={fetchTrips}
            actionId={actionId} validate={validate} reject={reject} gift={gift}
            pendingCount={pendingCount} validatedCount={validatedCount} paidCount={paidCount}
            setEditTrip={setEditTrip}
          />
        )}
        {activeTab === "analytiques" && <AdminAnalyticsTab />}
        {activeTab === "email" && <AdminEmailTab />}
      </main>

      {/* Trip Editor Modal */}
      {editTrip && (
        <AdminTripEditor
          tripId={editTrip.id}
          destination={editTrip.destination}
          onClose={() => setEditTrip(null)}
          onSaved={() => setEditTrip(null)}
        />
      )}
    </div>
  );
}
